import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import api from '../services/api';
import ExpenseModal from './ExpenseModal';

const ExpensesTab = ({ session, date }) => {
    const [dailyExpenses, setDailyExpenses] = useState(null);
    const [loading, setLoading] = useState(false);
    // Fix: Use standard Colombia date string per SKILL.md
    const getColombiaDate = () => {
        return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
    };

    const [selectedDate, setSelectedDate] = useState(date || getColombiaDate());
    const [showAddModal, setShowAddModal] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        if (date) {
            setSelectedDate(date);
        }
    }, [date]);

    useEffect(() => {
        fetchDailyExpenses();
    }, [selectedDate]);

    const fetchDailyExpenses = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({ date: selectedDate });
            if (session?.id) params.append('session_id', session.id);
            const response = await api.get(`/cash-box/daily-expenses?${params}`);
            setDailyExpenses(response.data);
        } catch (error) {
            console.error('Error fetching daily expenses:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const getTypeLabel = (type) => {
        const labels = {
            'supplier_payment': 'Pago Proveedor',
            'operational_expense': 'Gasto Operativo',
            'employee_advance': 'Anticipo Empleado',
            'bank_deposit': 'Consignación Banco',
            'withdrawal': 'Retiro',
            'other': 'Otro'
        };
        return labels[type] || type;
    };

    const getTypeIcon = (type) => {
        const icons = {
            'supplier_payment': '🏭',
            'operational_expense': '⚙️',
            'employee_advance': '👤',
            'bank_deposit': '🏦',
            'withdrawal': '💸',
            'other': '📝'
        };
        return icons[type] || '📤';
    };

    if (loading && !dailyExpenses) {
        return <div className="text-center py-8">Cargando egresos...</div>;
    }

    return (
        <div className="space-y-4">
            {/* Header with Add Button */}
            <div className="flex justify-between items-center">
                {!date ? (
                    <div className="flex items-center gap-4">
                        <label className="text-sm font-medium text-gray-700">Fecha:</label>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2"
                        />
                    </div>
                ) : (
                    <div className="text-lg font-semibold text-gray-700">
                        Egresos del {selectedDate}
                    </div>
                )}

                {session?.status === 'open' && (
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
                    >
                        <span>+</span>
                        <span>Registrar Egreso</span>
                    </button>
                )}
            </div>

            {/* Total Card */}
            <Card className="bg-gradient-to-r from-red-50 to-orange-50">
                <CardContent className="pt-4">
                    <div className="text-sm text-gray-600">Total Egresos de la Sesión</div>
                    <div className="text-3xl font-bold text-red-600">
                        {formatCurrency(dailyExpenses?.total)}
                    </div>
                </CardContent>
            </Card>

            {/* Expenses List */}
            {dailyExpenses?.expenses && dailyExpenses.expenses.length > 0 ? (
                <Card>
                    <CardHeader>
                        <CardTitle>📤 Movimientos de la Sesión</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {dailyExpenses.expenses.map((expense) => (
                                <div
                                    key={expense.id}
                                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xl">{getTypeIcon(expense.type)}</span>
                                                <span className="font-semibold">
                                                    {expense.concept || getTypeLabel(expense.type)}
                                                </span>
                                            </div>
                                            <div className="text-sm text-gray-600 space-y-1">
                                                {expense.beneficiary && (
                                                    <div>
                                                        <span className="font-medium">Beneficiario:</span> {expense.beneficiary}
                                                    </div>
                                                )}
                                                {expense.notes && (
                                                    <div>
                                                        <span className="font-medium">Notas:</span> {expense.notes}
                                                    </div>
                                                )}
                                                <div className="text-xs text-gray-500">
                                                    Registrado por: {expense.created_by} - {expense.time}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="text-right">
                                                <div className="text-xs text-gray-500">{getTypeLabel(expense.type)}</div>
                                                <div className="text-lg font-bold text-red-600">
                                                    {formatCurrency(expense.amount)}
                                                </div>
                                            </div>
                                            {expense.evidence_file && (
                                                <button
                                                    onClick={() => setPreviewImage(expense.evidence_file)}
                                                    className="text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-50"
                                                    title="Ver evidencia"
                                                >
                                                    📷
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardContent className="text-center py-8 text-gray-500">
                        No hay egresos registrados para esta fecha
                    </CardContent>
                </Card>
            )}

            {/* Image Preview Modal */}
            {previewImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                    onClick={() => setPreviewImage(null)}
                >
                    <div className="max-w-4xl max-h-screen p-4">
                        <img
                            src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${previewImage}`}
                            alt="Evidencia"
                            className="max-w-full max-h-full rounded"
                        />
                        <button
                            onClick={() => setPreviewImage(null)}
                            className="mt-4 bg-white text-gray-800 px-4 py-2 rounded hover:bg-gray-200"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}

            {/* Add Expense Modal */}
            {showAddModal && (
                <ExpenseModal
                    sessionId={session?.id}
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false);
                        fetchDailyExpenses();
                    }}
                />
            )}
        </div>
    );
};

export default ExpensesTab;
